import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin,
  Edit,
  Save,
  X,
  Camera,
  Award,
  BookOpen,
  Clock,
  Sparkles,
  Target,
  Zap,
  Shield,
  Star,
  TrendingUp,
  CheckCircle,
  Building2,
  UserCircle,
  UserCheck,
  Crown,
  Palette,
  Sparkles as SparklesIcon,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import traineeService, { TraineeProfile } from '../../services/trainee.service';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<TraineeProfile | null>(null);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await traineeService.getProfile();
      setProfile(data);
      setFormData({
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone || ''
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const updatedProfile = await traineeService.updateProfile(formData);
      setProfile(updatedProfile);
      setIsEditing(false);
    } catch (error) {
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        firstname: profile.firstname,
        lastname: profile.lastname,
        email: profile.email,
        phone: profile.phone || ''
      });
    }
    setIsEditing(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await traineeService.deleteAccount();
      // Clear auth data and logout
      logout();
      // Navigate to home page
      navigate('/');
    } catch (error) {
      alert('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getUserInitials = () => {
    if (profile) {
      return `${profile.firstname.charAt(0)}${profile.lastname.charAt(0)}`.toUpperCase();
    }
    return 'UP';
  };

  const getNFTUserIcon = () => {
    // NFT-style user icon with brand color palette
    const colors = [
      'from-brand-primary to-brand-secondary', // Purple to Pink
      'from-brand-secondary to-brand-mint',    // Pink to Mint
      'from-brand-mint to-brand-accent',       // Mint to Yellow
      'from-brand-accent to-brand-primary',    // Yellow to Purple
      'from-brand-primary to-brand-mint',      // Purple to Mint
      'from-brand-secondary to-brand-accent'   // Pink to Yellow
    ];
    
    // Use user ID to consistently show the same color
    const userHash = Number(profile?.id || user?.id || 0);
    const colorClass = colors[userHash % colors.length];
    
    return (
      <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center relative overflow-hidden shadow-2xl`}>
        {/* NFT-style background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-2 left-2 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute bottom-2 left-2 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute bottom-2 right-2 w-2 h-2 bg-white rounded-full"></div>
        </div>
        
        {/* Main icon */}
        <div className="relative z-10">
          <User className="h-12 w-12 text-white drop-shadow-lg" />
        </div>
        
        {/* NFT-style sparkle effects */}
        <div className="absolute top-1 right-1">
          <SparklesIcon className="h-3 w-3 text-brand-accent animate-pulse" />
        </div>
        <div className="absolute bottom-1 left-1">
          <SparklesIcon className="h-3 w-3 text-brand-accent animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>
        
        {/* NFT-style border glow */}
        <div className="absolute inset-0 rounded-2xl border-2 border-white/30"></div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title={t('trainee.profile.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout title={t('trainee.profile.title')}>
        <div className="text-center py-16">
          <p className="text-gray-600">{t('trainee.profile.errorLoading')}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('trainee.profile.title')}>
      <div className="space-y-4">
        {/* Welcome Section */}
        <div className="bg-gradient-to-br from-brand-primary/5 via-brand-accent/5 to-brand-surface rounded-xl p-4 border border-brand-primary/20 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-3">
                <div className="p-2 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg shadow-lg self-start">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-brand-primary to-brand-accent bg-clip-text text-transparent">
                    {t('trainee.profile.title')} 👤
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {t('trainee.profile.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded">
                  <Calendar className="h-3 w-3" />
                  <span>{t('trainee.profile.memberSince')} {new Date(profile.createdAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long' })}</span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded">
                  <Shield className="h-3 w-3" />
                  <span>{t('trainee.profile.accountVerified')}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="bg-green-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-green-700 transition-all shadow-md flex items-center gap-2 text-sm"
                  >
                    <Save className="h-4 w-4" />
                    {t('trainee.profile.actions.save')}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-gray-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-gray-700 transition-all shadow-md flex items-center gap-2 text-sm"
                  >
                    <X className="h-4 w-4" />
                    {t('trainee.profile.actions.cancel')}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-brand-primary text-white px-3 py-2 rounded-lg font-medium hover:bg-brand-primary/90 transition-all shadow-md flex items-center gap-2 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  {t('trainee.profile.actions.edit')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Full Width Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="p-6">
            {/* User Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
              <div className="relative self-center sm:self-auto">
                {/* Modern User Avatar */}
                <div className="w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-accent rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">
                    {getUserInitials()}
                  </span>
                </div>
                {isEditing && (
                  <button className="absolute -bottom-1 -right-1 bg-brand-primary text-white p-2 rounded-full hover:bg-brand-primary/90 transition-all shadow-lg">
                    <Camera className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="text-center sm:text-left flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  {`${formData.firstname} ${formData.lastname}`}
                </h1>
                <p className="text-gray-600 mb-2">{t('trainee.profile.userType')}</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                  <span>{t('trainee.profile.memberSince')} {new Date(profile.createdAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long' })}</span>
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">{t('trainee.profile.online')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('trainee.profile.fields.firstName')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.firstname}
                    onChange={(e) => handleInputChange('firstname', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all"
                  />
                ) : (
                  <div className="px-4 py-3 bg-gray-50 rounded-xl">
                    <p className="text-gray-900 font-medium">{formData.firstname}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('trainee.profile.fields.email')}
                </label>
                <div className="px-4 py-3 bg-gray-100 rounded-xl border border-gray-200">
                  <p className="text-gray-700 font-medium">{formData.email}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('trainee.profile.fields.emailNotEditable')}</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('trainee.profile.fields.lastName')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.lastname}
                    onChange={(e) => handleInputChange('lastname', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all"
                  />
                ) : (
                  <div className="px-4 py-3 bg-gray-50 rounded-xl">
                    <p className="text-gray-900 font-medium">{formData.lastname}</p>
                  </div>
                )}
              </div>


              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {t('trainee.profile.fields.phone')}
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all"
                  />
                ) : (
                  <div className="px-4 py-3 bg-gray-50 rounded-xl">
                    <p className="text-gray-900 font-medium">{formData.phone || t('trainee.profile.fields.notProvided')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Delete Account Section */}
            <div className="mt-8 pt-8 border-t border-red-200">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      Danger Zone
                    </h3>
                    <p className="text-sm text-red-700 mb-4">
                      Once you delete your account, there is no going back. All your data, including subscriptions, reviews, and personal information will be permanently deleted.
                    </p>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all shadow-md flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeleting ? 'Deleting...' : 'Delete My Account'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Delete Account?
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete:
            </p>
            
            <ul className="list-disc list-inside space-y-2 mb-6 text-sm text-gray-600">
              <li>All your personal information</li>
              <li>Your training subscriptions</li>
              <li>Your reviews and ratings</li>
              <li>Your training history</li>
              <li>All associated data</li>
            </ul>
            
            <p className="text-sm font-semibold text-red-600 mb-6">
              This action is permanent and cannot be reversed!
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Yes, Delete Account
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ProfilePage;
